import React, { useState } from 'react';
import { api } from '../lib/api.js';
import { rangeLabel } from './PeriodPicker.jsx';

/**
 * Saves a workbook the server lays out from rows the screen already has.
 * `build()` returns { filename, title?, subtitle?, sheets: [{ name, columns, rows }] }
 * and may be async, for a list that has to fetch its remaining pages first.
 */
export async function downloadXlsx(spec) {
  const res = await api.postRaw('/api/reports/xlsx', spec);
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${spec.filename || 'invit-export'}.xlsx`;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

/** "Oct 2026" or "All dates", for a sheet's subtitle and a file name. */
export const periodText = (period) => (period ? rangeLabel(period.from, period.to) : 'All dates');
export const periodSlug = (period) => (period ? `${period.from}-to-${period.to}` : 'all-dates');

/**
 * Every row of a paged list, not only the pages loaded on screen: the export
 * is of the filter, so it follows the cursor to the end.
 */
export async function fetchAllPages(url, key, max = 50000) {
  const out = [];
  let cursor = null;
  do {
    const sep = url.includes('?') ? '&' : '?';
    const page = await api.get(`${url}${sep}limit=200${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`);
    out.push(...(page[key] || []));
    cursor = page.has_more ? page.next_cursor : null;
  } while (cursor && out.length < max);
  return out;
}

export default function ExportButton({ build, label = 'Excel', small = false, disabled = false }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const run = async () => {
    setBusy(true); setError(null);
    try { await downloadXlsx(await build()); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  return (
    <span className="export-btn">
      <button type="button" className={`btn secondary${small ? ' small' : ''}`} disabled={busy || disabled} onClick={run}
        title="Download as an Excel workbook">
        <span aria-hidden="true">⤓</span> {busy ? 'Preparing…' : label}
      </button>
      {error && <span className="sub" role="alert" style={{ color: 'var(--critical-text)', marginLeft: 6 }}>{error}</span>}
    </span>
  );
}
